import { useEffect, useState, useContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import Header from "./_components/header/header";
import Main from "./pages/main";
import ApiService from "./_services/ApiService";
import googleAnalytics from "./_services/google-analytics/google-analytics.service";
import Config from "./_config/config";
import { AppPropertiesContext } from "./contexts/app-properties.context";
import Loader from "./_components/loader/loader";
import SideNavigation from "./_components/side-navigation/side-navigation.component";
import { AutoLogoutProvider } from "./contexts/auto-logout.context";
import SessionExpiredModal from "./_components/session-expired-modal/session-expired-modal.component";
import CredentialUnlockWatcher from "./_components/credential-unlock-watcher/CredentialUnlockWatcher";
import isKiosk from "./_services/kiosk/kiosk.service";
import UserService from "./_services/UserService";
import { isPublicPreloginPath } from "./_services/public-routes";
import { applyTheme } from "./_config/themeConfig";
import { applyTheme as applyTenantTheme } from "./theme/themeEngine";

// Initialize i18n
import "./i18n";

import "./App.css";

// Kiosk V2 shell — self-contained, does NOT use any existing kiosk components
const KioskApp = lazy(() => import("./pages/kiosk-app/KioskApp"));

// Atlas — career guidance sub-app for Class 12 students
const AtlasApp = lazy(() => import("./pages/atlas-app/AtlasApp"));

// Public credential verification — standalone, no auth
const CredentialVerify = lazy(() => import("./pages/credential-verify/CredentialVerify"));

// Public Voila Board — standalone, no auth
const PublicVoilaBoard = lazy(() => import("./pages/voila-board/PublicVoilaBoard"));

// Public product catalog — browsable pre-login (marketplace / lead-gen).
// These endpoints (v1/products/catalog, /:id/details, /:id/summary,
// /:id/brochure-lead) are all un-authed on nodefork; the only login-gated
// actions are the Apply/Subscribe CTAs, which self-redirect to login.
const Product = lazy(() => import("./pages/product/Product"));
const ProductList = lazy(() => import("./pages/product/ProductList"));
const ProductDetails = lazy(() => import("./pages/product/ProductDetails"));

function App() {
  const {
    addDataToTests,
    addRegistrationForms,
    addHideStudentDossier,
    addLoginText1,
    addAppProperties,
    markAppPropertiesLoaded,
  } = useContext(AppPropertiesContext);
  const [loginRoute, setloginRoute] = useState("");
  const [homeRoute, setHomeRoute] = useState("");
  const [suppressLogs, setSuppressLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  const initGoogleAnalytics = async () => {
    try {
      const res = await ApiService.fetchGoogleAnalytics();
      console.log(res);
      const appProperties =
        typeof res.data === "string" ? JSON.parse(res.data || "{}") : res.data;

      const {
        "GA-Code": id,
        "Home-Route": homeRouteLegacy,
        "Login-Route": loginRoute,
        "Home-Title": homeTitle,
        "Suppress-Home-Page-Logs": suppressLogs,
        "Mobile-Login-Footer": loginFooter,
        "Portlet-Url": testCacheUrl,
        "Registration-Forms": registrationForms,
        "Hide-Student-Dossier": hideStudentDossier,
        sebBrowserHomeRoute,
        Tests,
        "Login-Text1": loginText1,
      } = appProperties;

      // Start-page route. iteach's Tenant Preferences → Start Page picker writes
      // the camelCase `homeRoute` key; older tenant files carry the hyphenated
      // `Home-Route`. Accept either so the picker's value actually takes effect.
      const homeRoute = appProperties.homeRoute || homeRouteLegacy;

      if (loginText1) {
        addLoginText1(loginText1);
      }

      if (Tests) {
        addDataToTests(Tests);
      }

      if (appProperties) {
        addAppProperties(appProperties);
        // Re-pin the favicon now that we know the tenant's branded PWA icons.
        updateFavIcon(appProperties);
        // Apply the tenant's theme (preset + brand overrides + dark-mode policy)
        // and refresh the pre-paint boot cache (see public/index.html).
        applyTenantTheme(appProperties.theme);
      }

      addHideStudentDossier(hideStudentDossier ? true : false);

      console.log(id, homeRoute);
      console.log(homeTitle, suppressLogs);

      console.log(registrationForms, "form");

      document.title = homeTitle || Config.siteTitle;
      googleAnalytics(id);
      setloginRoute(loginRoute);
      setHomeRoute(homeRoute);
      if (homeRoute) sessionStorage.setItem("homeRoute", homeRoute);
      if (sebBrowserHomeRoute) sessionStorage.setItem("sebBrowserHomeRoute", sebBrowserHomeRoute);
      if (loginRoute) sessionStorage.setItem("loginRoute", loginRoute);
      if (loginFooter) sessionStorage.setItem("loginFooter", loginFooter);
      if (testCacheUrl) sessionStorage.setItem("testCacheUrl", testCacheUrl);
      if (homeTitle) sessionStorage.setItem("homeTitle", homeTitle);
      if (registrationForms) addRegistrationForms(registrationForms);
      if (suppressLogs !== undefined) setSuppressLogs(suppressLogs);
    } catch (err) {
      console.log(err);
      // Fetch (or parsing) failed — mark the load settled so consumers that
      // wait on appPropertiesLoaded (e.g. Footer) can fall back.
      markAppPropertiesLoaded();
    }
  };

  // Cache the tenant's iLearnAppConfig (customer_detail_master.iLearnAppConfig)
  // once at startup so non-React code (ApiService) can read feature flags
  // synchronously — e.g. onlineTest.backend, which routes the online-test calls
  // to the Java backend (default) or the new Node endpoints.
  const cacheIlearnAppConfig = async () => {
    try {
      const res = await ApiService.getIlearnAppConfig();
      if (res?.data?.success && res.data.config) {
        sessionStorage.setItem("iLearnAppConfig", JSON.stringify(res.data.config));
      }
    } catch (err) {
      // Best-effort — absence of the cache defaults every flag to its safe value.
      console.log("iLearnAppConfig cache failed", err);
    }
  };

  const updateFavIcon = (appProperties) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    // Prefer the PWA icon URLs the tenant uploaded via iteach Tenant Preferences
    // → Branding & Logos (mirrored into app-properties.json.pwa on save). Falls
    // back to the legacy convention <bucket>/<tenant>/files/logos/favicon.ico,
    // which 404s for most tenants and leaves the browser on the bundled GTET
    // favicon — exactly what we want to escape.
    const pwa = appProperties?.pwa || {};
    const trim = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
    const brandFavicon =
      trim(pwa.icon192Url) || trim(pwa.iconUrl) || trim(pwa.icon512Url);
    const url =
      brandFavicon ||
      `https://tuningfork-live.s3.ap-southeast-1.amazonaws.com/${Config.siteTitle}/files/logos/favicon.ico`;
    link.href = url;
  };

  const updateBodyBackground = () => {
    document.body.style = `background:url(https://tuningfork-live.s3.ap-southeast-1.amazonaws.com/${Config.siteTitle}/files/logos/watermark1.jpg)`;
  };

  const addPaymentRecordsShortcut = () => {
    if (!isKiosk()) return;
    document.addEventListener("keydown", function (e) {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault(); // Prevent default browser behavior if any
        // Add your custom logic here
        window.location = "/payment-records";
      }
    });
  };

  const checkValidUser = async () => {
    // Atlas sub-app uses its own auth flow — skip main-app user verification
    if (window.location.pathname.startsWith('/atlas')) return;
    // Voila Board public view — no auth required
    if (window.location.pathname.startsWith('/voila/')) return;
    // Quote Builder — public quote page, student views without logging in
    if (window.location.pathname.startsWith('/quote-builder')) return;
    // Product catalog/details — public marketplace, browsable without login.
    // A stale `user` object (token gone) must not trigger a verify→/logout bounce.
    if (isPublicPreloginPath(window.location.pathname)) return;

    if(!localStorage.getItem("user")) return;

    try {
      const response = await ApiService.verifyValidUser();
      console.log("verifyUser response.data:", JSON.stringify(response.data));
      if (response.data.message?.toLowerCase() === "success" && response.data.data) {
        return;
      }

      if(window.location.pathname === "/logout") return;
      return (window.location = "/logout");
    } catch (err) {
      console.log(err);
      return (window.location = "/logout");
    }
  };

  useEffect(() => {
    applyTheme();
    checkValidUser();
    // Boot gate: keep the app shell on <Loader /> until BOTH tenant configs
    // settle — app-properties.json (homeRoute, branding) and iLearnAppConfig
    // (feature flags). Rendering before the flag cache is seeded makes every
    // flag read as default-on, flashing admin-disabled features (e.g. Games
    // Zone) and the wrong start page. Both fetches still run in parallel;
    // failures are caught inside each, so this always settles.
    Promise.allSettled([initGoogleAnalytics(), cacheIlearnAppConfig()]).then(
      () => setLoading(false)
    );
    updateFavIcon();
    updateBodyBackground();
    addPaymentRecordsShortcut();
  }, []);

  return (
    <div id="App">
      <div id="page-wrap">
        <div className="container1">
          <Header />
          <Router>
            <SessionExpiredModal />
            <CredentialUnlockWatcher />
            <Switch>
              {/* Public credential verification — no auth, no app shell */}
              <Route path="/verify/:credentialId">
                <Suspense fallback={<Loader />}>
                  <CredentialVerify />
                </Suspense>
              </Route>

              {/* Public Voila Board — no auth, standalone timeline view */}
              <Route path="/voila/:boardUUID">
                <Suspense fallback={<Loader />}>
                  <PublicVoilaBoard />
                </Suspense>
              </Route>

              {/* Legacy /saarthi/* bookmarks → forward to the new /atlas/* paths.
                  Must be declared before the /atlas Route so the rewrite
                  fires before AtlasApp mounts. */}
              <Route
                path="/saarthi"
                render={({ location }) => (
                  <Redirect
                    to={{
                      pathname: location.pathname.replace(/^\/saarthi/, "/atlas"),
                      search: location.search,
                      hash: location.hash,
                    }}
                  />
                )}
              />

              {/* Atlas — career guidance sub-app, bypasses main shell */}
              <Route path="/atlas">
                <Suspense fallback={<Loader />}>
                  <AtlasApp />
                </Suspense>
              </Route>

              {/* Public product catalog — browsable without login.
                  Only mounted when logged out; logged-in users fall through
                  to the <Main> shell below so they keep the sidebar/app chrome
                  (no regression to the in-app product experience). */}
              {!UserService.getToken() && (
                <Route path={["/Products", "/ProductDetails"]}>
                  <Suspense fallback={<Loader />}>
                    <Switch>
                      <Route path="/Products" exact component={Product} />
                      <Route
                        path="/Products/:id/:title"
                        exact
                        component={ProductList}
                      />
                      <Route
                        path="/ProductDetails/:productId?"
                        component={ProductDetails}
                      />
                    </Switch>
                  </Suspense>
                </Route>
              )}

              {/* Kiosk V2 shell — bypasses Header, SideNav, and production AutoLogout */}
              <Route path="/kiosk">
                <Suspense fallback={<Loader />}>
                  <KioskApp />
                </Suspense>
              </Route>

              {/* Regular app — unchanged */}
              <Route>
                <AutoLogoutProvider>
                  {loading ? (
                    <Loader />
                  ) : (
                    <SideNavigation>
                      <Main
                        loginRoute={loginRoute}
                        homeRoute={homeRoute}
                        suppressLogs={suppressLogs}
                      />
                    </SideNavigation>
                  )}
                </AutoLogoutProvider>
              </Route>
            </Switch>
          </Router>
        </div>
      </div>
    </div>
  );
}

export default App;
